/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: TimelineGantt.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-TIMELINE
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * TimelineGantt - Gantt chart; label column width follows sidebar (UC-018).
 * getLabelCellSx - sticky label cell sx with animated width.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-28 - чуть меньший шрифт названий фич (0.75rem).
 * END_CHANGE_SUMMARY
 */

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { alpha } from '@mui/material/styles';
import {
  formatDayMonth,
  formatWeekdayShort,
  isSameDay,
  toIsoDate,
} from '../../../utils/dateUtils';
import { getColorByName } from '../../../utils/projectColors';
import { createDrawerWidthTransition } from '../../../theme/appTheme';
import {
  formatDayHours,
  formatFeatureSpanRange,
  formatHours,
  getAbsenceForDate,
  isIsoWithinInclusiveSpan,
  isSameTimelineParticipant,
  isTimelineDayOff,
  TIMELINE_DAY_COLUMN_WIDTH,
  TIMELINE_LABEL_COLUMN_WIDTH,
  TIMELINE_VIEW_MODE,
} from '../timelineUtils';
import { actualHoursKey } from '../actualScheduleUtils';

const DAY_WIDTH = TIMELINE_DAY_COLUMN_WIDTH;

const dayOffBg = 'grey.100';
const dayOffHeaderBg = 'grey.200';
const vacationBg = 'grey.400';
const featureRowBg = 'grey.50';
const workCellBg = '#90a9b0';
const overviewBarBg = '#5a7d8a';
const overviewBarMutedBg = (theme) => alpha(overviewBarBg, 0.42);
const todayBg = (theme) => alpha(theme.palette.primary.main, 0.08);
const WORK_HIGHLIGHT_WIDTH = 2;
const WORK_HIGHLIGHT_TRANSITION = 'box-shadow 0.2s ease-in-out';

/* START_METHOD_getWorkCellHighlightSx */
/* START_CONTRACT: getWorkCellHighlightSx
 * PURPOSE: Вычислить sx-стиль подсветки рабочей ячейки с учётом соседних подсвеченных ячеек.
 * INPUTS: { highlighted: boolean, prevHighlighted: boolean, nextHighlighted: boolean }.
 * OUTPUTS: { object|function - MUI sx для boxShadow }.
 * SIDE_EFFECTS: нет (чистая функция).
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: getWorkCellHighlightSx
 */
function getWorkCellHighlightSx(highlighted, prevHighlighted, nextHighlighted) {
  if (!highlighted) {
    return {
      boxShadow: 'none',
      transition: WORK_HIGHLIGHT_TRANSITION,
    };
  }

  return (theme) => {
    const color = theme.palette.primary.dark;
    const shadows = [
      `inset 0 ${WORK_HIGHLIGHT_WIDTH}px 0 0 ${color}`,
      `inset 0 -${WORK_HIGHLIGHT_WIDTH}px 0 0 ${color}`,
    ];
    if (!prevHighlighted) {
      shadows.push(`inset ${WORK_HIGHLIGHT_WIDTH}px 0 0 0 ${color}`);
    }
    if (!nextHighlighted) {
      shadows.push(`inset -${WORK_HIGHLIGHT_WIDTH}px 0 0 0 ${color}`);
    }
    return {
      boxShadow: shadows.join(', '),
      transition: WORK_HIGHLIGHT_TRANSITION,
      zIndex: 2,
      position: 'relative',
    };
  };
}
/* END_METHOD_getWorkCellHighlightSx */

const employeeRowSx = {
  display: 'flex',
  borderBottom: '1px solid',
  borderColor: 'divider',
};

const dayCellBase = {
  flex: `0 0 ${DAY_WIDTH}px`,
  width: DAY_WIDTH,
  minWidth: DAY_WIDTH,
  maxWidth: DAY_WIDTH,
  borderRight: '1px solid',
  borderColor: 'divider',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const labelCellBase = {
  flexShrink: 0,
  position: 'sticky',
  left: 0,
  zIndex: 1,
  borderRight: '1px solid',
  borderColor: 'divider',
  px: 1.5,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

/* START_METHOD_getLabelCellSx */
/* START_CONTRACT: getLabelCellSx
 * PURPOSE: sx sticky label-ячейки с анимируемой шириной (UC-018).
 * INPUTS: { labelWidth: number }.
 * OUTPUTS: { object - MUI sx }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE, UC-018
 * END_CONTRACT: getLabelCellSx
 */
function getLabelCellSx(labelWidth) {
  return {
    ...labelCellBase,
    width: labelWidth,
    minWidth: labelWidth,
    maxWidth: labelWidth,
    flex: `0 0 ${labelWidth}px`,
    transition: (theme) => createDrawerWidthTransition(theme),
  };
}
/* END_METHOD_getLabelCellSx */

/* START_METHOD_buildColumns */
/* START_CONTRACT: buildColumns
 * PURPOSE: Построить массив колонок таймлайна с метаданными (рабочие часы, выходной, сегодня).
 * INPUTS: { dates: Date[], dayOffSet: Set<string>, workingHoursByDate: Map<string,number>, today: Date }.
 * OUTPUTS: { Array<{date, iso, workingHours, isDayOff, isToday}> }.
 * SIDE_EFFECTS: нет (чистая функция).
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: buildColumns
 */
function buildColumns(dates, dayOffSet, workingHoursByDate, today) {
  return dates.map((date) => {
    const iso = toIsoDate(date);
    return {
      date,
      iso,
      workingHours: workingHoursByDate.get(iso) ?? null,
      isDayOff: isTimelineDayOff(date, dayOffSet, workingHoursByDate),
      isToday: isSameDay(date, today),
    };
  });
}
/* END_METHOD_buildColumns */

/* START_METHOD_getBodyCellBg */
/* START_CONTRACT: getBodyCellBg
 * PURPOSE: Определить цвет фона ячейки строки фичи по приоритету статусов.
 * INPUTS: { column: Column, isVacation: boolean }.
 * OUTPUTS: { string - цвет/токен MUI }.
 * SIDE_EFFECTS: нет (чистая функция).
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: getBodyCellBg
 */
function getBodyCellBg(column, isVacation) {
  if (isVacation) {
    return vacationBg;
  }
  if (column.isDayOff) {
    return dayOffBg;
  }
  if (column.isToday) {
    return todayBg;
  }
  return 'transparent';
}
/* END_METHOD_getBodyCellBg */

/* START_METHOD_getEmployeeCellBg */
/* START_CONTRACT: getEmployeeCellBg
 * PURPOSE: Определить цвет фона ячейки строки сотрудника по приоритету статусов.
 * INPUTS: { column: Column, isVacation: boolean, isLoaded: boolean }.
 * OUTPUTS: { string - цвет/токен MUI }.
 * SIDE_EFFECTS: нет (чистая функция).
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: getEmployeeCellBg
 */
function getEmployeeCellBg(column, isVacation, isLoaded) {
  if (isVacation) {
    return vacationBg;
  }
  if (column.isDayOff) {
    return dayOffBg;
  }
  if (isLoaded) {
    return workCellBg;
  }
  if (column.isToday) {
    return todayBg;
  }
  return 'transparent';
}
/* END_METHOD_getEmployeeCellBg */

/* START_METHOD_isWithinLoad */
/* START_CONTRACT: isWithinLoad
 * PURPOSE: Проверить, попадает ли дата в интервал загрузки сотрудника.
 * INPUTS: { iso: string, startIso: string, endIso: string }.
 * OUTPUTS: { boolean }.
 * SIDE_EFFECTS: нет (чистая функция).
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: isWithinLoad
 */
function isWithinLoad(iso, startIso, endIso) {
  return Boolean(startIso && iso >= startIso && iso <= endIso);
}
/* END_METHOD_isWithinLoad */

/* START_METHOD_EmployeeTimelineRow */
/* START_CONTRACT: EmployeeTimelineRow
 * PURPOSE: Строка сотрудника в диаграмме Ганта: ячейки загрузки/отсутствий, подсветка, обработка кликов.
 * INPUTS: { row, group, columns, highlightedParticipant, onParticipantHover, onAbsenceClick, onEstimateClick }.
 * OUTPUTS: { JSX.Element - строка }.
 * SIDE_EFFECTS: локальный state ховера; делегирует колбэки наверх.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: EmployeeTimelineRow
 */
function EmployeeTimelineRow({
  row,
  group,
  columns,
  labelWidth,
  highlightedParticipant,
  onParticipantHover,
  onAbsenceClick,
  onEstimateClick,
  onActualHoursClick,
  actualHoursByKey,
}) {
  const [hoveredAbsenceId, setHoveredAbsenceId] = useState(null);

  const isParticipantHighlighted = Boolean(
    highlightedParticipant
    && isSameTimelineParticipant(row, highlightedParticipant),
  );

  /* START_METHOD_handleParticipantHover */
  /* START_CONTRACT: handleParticipantHover
   * PURPOSE: Сообщить наверх о наведении на участника (сотрудник+роль) для подсветки.
   * INPUTS: {}.
   * OUTPUTS: { void }.
   * SIDE_EFFECTS: вызов onParticipantHover.
   * LINKS: M-FE-TIMELINE
   * END_CONTRACT: handleParticipantHover
   */
  const handleParticipantHover = () => {
    onParticipantHover({
      employeeId: row.employeeId,
      projectRoleId: row.projectRoleId,
    });
  };
  /* END_METHOD_handleParticipantHover */

  const workHighlightFlags = columns.map((column) => {
    const absence = getAbsenceForDate(column.date, row.absences);
    const isVacation = Boolean(absence);
    const hasStoredActual = Boolean(actualHoursByKey?.get(
      actualHoursKey(row.featureId, row.projectRoleId, column.iso),
    ));
    const inLoadSpan = !isVacation
      && !column.isDayOff
      && isWithinLoad(column.iso, row.loadStartIso, row.loadEndIso);
    return isParticipantHighlighted
      && (inLoadSpan || hasStoredActual);
  });

  return (
    <Box
      sx={employeeRowSx}
      onMouseEnter={handleParticipantHover}
      onMouseLeave={() => setHoveredAbsenceId(null)}
    >
      <Box
        sx={{
          ...getLabelCellSx(labelWidth),
          bgcolor: 'background.paper',
          py: 0.5,
          pl: 3,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            variant="body2"
            color={row.assigned ? 'text.primary' : 'text.secondary'}
            noWrap
            title={row.assigned ? row.name : `${row.name} (роль не назначена)`}
            sx={{ minWidth: 0 }}
          >
            {row.name}
          </Typography>
          {row.roleShortName ? (
            <RoleShortNameChip
              label={row.roleShortName}
              projectRoleId={row.projectRoleId}
            />
          ) : null}
        </Box>
        <Typography
          component="button"
          type="button"
          variant="caption"
          color="text.secondary"
          onClick={() => onEstimateClick(group, row)}
          title="Нажмите для редактирования оценки"
          sx={{
            flexShrink: 0,
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            px: 0.5,
            py: 0.25,
            borderRadius: 1,
            fontSize: '0.65rem',
            lineHeight: 1.2,
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
            },
          }}
        >
          {row.hasStoredActuals
            ? `${formatHours(row.hours)} / ${formatHours(row.effectiveTotal)}`
            : formatHours(row.hours)}
        </Typography>
      </Box>
      {columns.map((column, columnIndex) => {
        const absence = getAbsenceForDate(column.date, row.absences);
        const isVacation = Boolean(absence);
        const clickableVacation = isVacation && Boolean(row.employeeId);
        const isHovered = clickableVacation && absence.id === hoveredAbsenceId;
        const actualRecord = actualHoursByKey?.get(
          actualHoursKey(row.featureId, row.projectRoleId, column.iso),
        );
        const hasStoredActual = Boolean(actualRecord);
        const inLoadSpan = !isVacation
          && !column.isDayOff
          && isWithinLoad(column.iso, row.loadStartIso, row.loadEndIso);
        const isActiveCell = !isVacation && !column.isDayOff && (inLoadSpan || hasStoredActual);
        const isWorkHighlighted = workHighlightFlags[columnIndex];
        const prevWorkHighlighted = columnIndex > 0 && workHighlightFlags[columnIndex - 1];
        const nextWorkHighlighted = columnIndex < columns.length - 1
          && workHighlightFlags[columnIndex + 1];
        const dayHours = isActiveCell
          ? (hasStoredActual
            ? actualRecord.actualHours
            : row.hoursByDate?.get(column.iso) ?? null)
          : null;
        const plannedDayHours = row.plannedHoursByDate?.get(column.iso) ?? row.hoursByDate?.get(column.iso) ?? 0;
        const clickableActual = !isVacation;
        const handleClick = clickableVacation
          ? () => onAbsenceClick(row, absence)
          : clickableActual
            ? () => onActualHoursClick(group, row, column.iso, plannedDayHours ?? 0, actualRecord)
            : undefined;
        return (
          <Box
            key={column.iso}
            role={handleClick ? 'button' : undefined}
            tabIndex={handleClick ? 0 : undefined}
            onClick={handleClick}
            onMouseEnter={clickableVacation
              ? () => setHoveredAbsenceId(absence.id)
              : () => setHoveredAbsenceId(null)}
            onKeyDown={handleClick
              ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }
              : undefined}
            sx={[
              {
                ...dayCellBase,
                boxSizing: 'border-box',
                bgcolor: isHovered
                  ? 'grey.500'
                  : getEmployeeCellBg(column, isVacation, isActiveCell),
                ...(handleClick && { cursor: 'pointer' }),
                ...(hasStoredActual && isActiveCell && {
                  outline: '1px solid',
                  outlineColor: 'warning.main',
                  outlineOffset: -1,
                }),
              },
              getWorkCellHighlightSx(
                isWorkHighlighted,
                prevWorkHighlighted,
                nextWorkHighlighted,
              ),
            ]}
            title={isVacation
              ? `${row.name}: отсутствие${clickableVacation ? ' (нажмите для редактирования)' : ''}`
              : dayHours != null
                ? `${row.name}: ${formatHours(dayHours)} над «${group.featureName}»${hasStoredActual ? ' (факт)' : ''} — нажмите для редактирования`
                : `${row.name}: «${group.featureName}» — нажмите для ввода фактических часов`}
          >
            {dayHours != null ? (
              <Typography
                variant="caption"
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: 'text.primary',
                  userSelect: 'none',
                }}
              >
                {formatDayHours(dayHours)}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Box>
  );
}
/* END_METHOD_EmployeeTimelineRow */

/* START_METHOD_RoleShortNameChip */
/* START_CONTRACT: RoleShortNameChip
 * PURPOSE: Цветной чип с коротким названием роли (цвет детерминирован по имени/id).
 * INPUTS: { label: string, projectRoleId: number }.
 * OUTPUTS: { JSX.Element - Chip }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-TIMELINE
 * END_CONTRACT: RoleShortNameChip
 */
function RoleShortNameChip({ label, projectRoleId }) {
  const colorKey = label || String(projectRoleId);
  const color = getColorByName(colorKey);

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        flexShrink: 0,
        height: 20,
        bgcolor: color,
        color: '#fff',
        '& .MuiChip-label': {
          px: 0.75,
          fontSize: '0.65rem',
        },
      }}
    />
  );
}
/* END_METHOD_RoleShortNameChip */

/* START_METHOD_FeatureOverviewRow */
/* START_CONTRACT: FeatureOverviewRow
 * PURPOSE: Строка задачи в режиме сводки: полоса Ганта от раннего старта до позднего завершения (план+факт).
 * INPUTS: { group, columns, onFeatureClick }.
 * OUTPUTS: { JSX.Element - строка сводки }.
 * SIDE_EFFECTS: делегирует onFeatureClick.
 * LINKS: M-FE-TIMELINE, UC-017
 * END_CONTRACT: FeatureOverviewRow
 */
function FeatureOverviewRow({ group, columns, labelWidth, onFeatureClick }) {
  const spanRangeLabel = formatFeatureSpanRange(group.loadStartIso, group.loadEndIso);
  const inSpanFlags = columns.map((column) => (
    isIsoWithinInclusiveSpan(column.iso, group.loadStartIso, group.loadEndIso)
  ));

  return (
    <Box
      sx={{
        display: 'flex',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          ...getLabelCellSx(labelWidth),
          bgcolor: featureRowBg,
          py: 0.5,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <Typography
          component="button"
          type="button"
          variant="body2"
          noWrap
          title={group.featureName}
          onClick={() => onFeatureClick(group.featureId)}
          sx={{
            fontWeight: 700,
            fontSize: '0.75rem',
            lineHeight: 1.2,
            border: 'none',
            bgcolor: 'transparent',
            cursor: 'pointer',
            p: 0,
            m: 0,
            textAlign: 'left',
            minWidth: 0,
            flex: '1 1 auto',
            color: 'text.primary',
            '&:hover': {
              color: 'primary.main',
              textDecoration: 'underline',
              textDecorationStyle: 'dotted',
            },
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              borderRadius: 0.5,
            },
          }}
        >
          {group.featureName}
        </Typography>
        {spanRangeLabel ? (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            title={spanRangeLabel}
            sx={{ flexShrink: 0, fontSize: '0.65rem', lineHeight: 1.2 }}
          >
            {spanRangeLabel}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontSize: '0.65rem' }}>
            Нет оценок
          </Typography>
        )}
      </Box>
      {columns.map((column, columnIndex) => {
        const inSpan = inSpanFlags[columnIndex];
        const isSpanStart = inSpan && (columnIndex === 0 || !inSpanFlags[columnIndex - 1]);
        const isSpanEnd = inSpan && (
          columnIndex === columns.length - 1 || !inSpanFlags[columnIndex + 1]
        );
        let bgcolor = getBodyCellBg(column, false);
        if (inSpan) {
          bgcolor = column.isDayOff ? overviewBarMutedBg : overviewBarBg;
        }
        return (
          <Box
            key={column.iso}
            title={inSpan && spanRangeLabel
              ? `${group.featureName}: ${spanRangeLabel}`
              : undefined}
            sx={{
              ...dayCellBase,
              bgcolor,
              ...(inSpan && {
                borderTop: `2px solid ${overviewBarBg}`,
                borderBottom: `2px solid ${overviewBarBg}`,
                ...(isSpanStart && {
                  borderLeft: `2px solid ${overviewBarBg}`,
                  borderTopLeftRadius: 4,
                  borderBottomLeftRadius: 4,
                }),
                ...(isSpanEnd && {
                  borderRight: `2px solid ${overviewBarBg}`,
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                }),
              }),
            }}
          />
        );
      })}
    </Box>
  );
}
/* END_METHOD_FeatureOverviewRow */

/* START_METHOD_TimelineGantt */
/* START_CONTRACT: TimelineGantt
 * PURPOSE: Диаграмма Ганта таймлайна: шапка дат, группы фич и строки сотрудников с загрузкой.
 * INPUTS: { dates, dayOffSet, workingHoursByDate, rows, today, viewMode, labelWidth, onAbsenceClick, onEstimateClick, onFeatureClick }.
 * OUTPUTS: { JSX.Element - таблица Ганта }.
 * SIDE_EFFECTS: локальный state подсветки участника; делегирует колбэки наверх.
 * LINKS: M-FE-TIMELINE, UC-018
 * END_CONTRACT: TimelineGantt
 */
export default function TimelineGantt({
  dates,
  dayOffSet,
  workingHoursByDate,
  rows,
  today,
  viewMode = TIMELINE_VIEW_MODE.DETAILED,
  labelWidth = TIMELINE_LABEL_COLUMN_WIDTH,
  onAbsenceClick,
  onEstimateClick,
  onFeatureClick,
  onActualHoursClick,
  actualHoursByKey,
  scrollToTodayKey = 0,
}) {
  const isOverview = viewMode === TIMELINE_VIEW_MODE.OVERVIEW;
  const [highlightedParticipant, setHighlightedParticipant] = useState(null);
  const scrollContainerRef = useRef(null);

  const columns = useMemo(
    () => buildColumns(dates, dayOffSet, workingHoursByDate, today),
    [dates, dayOffSet, workingHoursByDate, today],
  );

  const todayColumnIndex = useMemo(
    () => columns.findIndex((column) => column.isToday),
    [columns],
  );

  useEffect(() => {
    if (todayColumnIndex < 0) {
      return;
    }
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const todayLeft = labelWidth + todayColumnIndex * DAY_WIDTH;
    const targetScroll = Math.max(0, todayLeft - container.clientWidth * 0.35);
    container.scrollTo({ left: targetScroll, behavior: scrollToTodayKey === 0 ? 'auto' : 'smooth' });
  }, [todayColumnIndex, scrollToTodayKey, columns.length, labelWidth]);

  const innerMinWidth = labelWidth + columns.length * DAY_WIDTH;

  return (
    <Paper ref={scrollContainerRef} variant="outlined" sx={{ overflowX: 'auto', width: '100%' }}>
      <Box
        sx={{
          minWidth: innerMinWidth,
          transition: (theme) => createDrawerWidthTransition(theme),
        }}
        onMouseLeave={() => setHighlightedParticipant(null)}
      >
        {/* Шапка с датами */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box
            sx={{
              ...getLabelCellSx(labelWidth),
              zIndex: 2,
              bgcolor: 'background.paper',
              py: 0.75,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.7rem' }}>
              {isOverview ? 'Задача' : 'Фича / Сотрудник'}
            </Typography>
          </Box>
          {columns.map((column) => (
            <Box
              key={column.iso}
              sx={{
                ...dayCellBase,
                flexDirection: 'column',
                gap: 0,
                py: 0.5,
                bgcolor: column.isToday
                  ? 'primary.main'
                  : column.isDayOff
                    ? dayOffHeaderBg
                    : 'background.paper',
                color: column.isToday ? 'primary.contrastText' : 'text.primary',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: 9, lineHeight: 1.1, opacity: 0.8 }}>
                {formatWeekdayShort(column.date)}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontSize: 10, fontWeight: 600, lineHeight: 1.1 }}
              >
                {formatDayMonth(column.date)}
              </Typography>
              {column.workingHours != null ? (
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: 9,
                    lineHeight: 1.1,
                    ...(column.workingHours === 0
                      ? column.isToday
                        ? { opacity: 0.55 }
                        : { color: 'text.disabled' }
                      : { opacity: 0.8 }),
                  }}
                >
                  {column.workingHours}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>

        {/* Тело: группы по фичам */}
        {rows.map((group) => (
          isOverview ? (
            <FeatureOverviewRow
              key={group.featureId}
              group={group}
              columns={columns}
              labelWidth={labelWidth}
              onFeatureClick={onFeatureClick}
            />
          ) : (
          <Fragment key={group.featureId}>
            <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ ...getLabelCellSx(labelWidth), bgcolor: featureRowBg, py: 1 }}>
                <Typography
                  component="button"
                  type="button"
                  variant="body2"
                  noWrap
                  title={group.featureName}
                  onClick={() => onFeatureClick(group.featureId)}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    lineHeight: 1.2,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    p: 0,
                    m: 0,
                    textAlign: 'left',
                    width: '100%',
                    color: 'text.primary',
                    '&:hover': {
                      color: 'primary.main',
                      textDecoration: 'underline',
                      textDecorationStyle: 'dotted',
                    },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      borderRadius: 0.5,
                    },
                  }}
                >
                  {group.featureName}
                </Typography>
              </Box>
              {columns.map((column) => (
                <Box
                  key={column.iso}
                  sx={{
                    ...dayCellBase,
                    bgcolor: column.isDayOff
                      ? dayOffBg
                      : column.isToday
                        ? todayBg
                        : featureRowBg,
                  }}
                />
              ))}
            </Box>

            {group.employeeRows.length === 0 && (
              <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ ...getLabelCellSx(labelWidth), bgcolor: 'background.paper', py: 0.75, pl: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Нет оценок
                  </Typography>
                </Box>
                {columns.map((column) => (
                  <Box
                    key={column.iso}
                    sx={{
                      ...dayCellBase,
                      bgcolor: getBodyCellBg(column, false),
                    }}
                  />
                ))}
              </Box>
            )}

            {group.employeeRows.map((row) => (
              <EmployeeTimelineRow
                key={row.key}
                row={row}
                group={group}
                columns={columns}
                labelWidth={labelWidth}
                highlightedParticipant={highlightedParticipant}
                onParticipantHover={setHighlightedParticipant}
                onAbsenceClick={onAbsenceClick}
                onEstimateClick={onEstimateClick}
                onActualHoursClick={onActualHoursClick}
                actualHoursByKey={actualHoursByKey}
              />
            ))}
          </Fragment>
          )
        ))}
      </Box>
    </Paper>
  );
}
/* END_METHOD_TimelineGantt */
